import { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw, Maximize2, Crop } from "lucide-react";

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const WHEEL_STEP = 0.15;

/**
 * ZoomablePreview
 * Renders an <img> thumbnail. Clicking (or the maximise button) opens a
 * fullscreen modal with wheel/pinch zoom and drag-to-pan so users can inspect
 * small VIN/tag characters before submitting to OCR.
 */
export const ZoomablePreview = ({ src, alt = "preview", className = "", testid = "zoomable-preview", onCrop, cropLabel = "Use this crop for OCR" }) => {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [cropping, setCropping] = useState(false);
  const dragRef = useRef(null);
  const imgWrapRef = useRef(null);
  const pinchRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
    // React state setters are stable; MIN/MAX_SCALE are module constants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(MAX_SCALE, s + 0.5));
      if (e.key === "-") setScale((s) => Math.max(MIN_SCALE, s - 0.5));
      if (e.key === "0") { setScale(1); setOffset({ x: 0, y: 0 }); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // Functional setState updates don't read `scale`/`offset` from closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clampOffset = (nextScale, next) => {
    // Allow panning proportional to how zoomed we are.
    const wrap = imgWrapRef.current;
    if (!wrap) return next;
    const bounds = 300 * nextScale;
    return {
      x: Math.max(-bounds, Math.min(bounds, next.x)),
      y: Math.max(-bounds, Math.min(bounds, next.y)),
    };
  };

  const onWheel = (e) => {
    e.preventDefault();
    setScale((prev) => {
      const dir = e.deltaY > 0 ? -1 : 1;
      return Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev + dir * WHEEL_STEP));
    });
  };

  const onMouseDown = (e) => {
    if (scale === 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clampOffset(scale, { x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy }));
  };
  const onMouseUp = () => { dragRef.current = null; };

  const distance = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { baseDist: distance(e.touches), baseScale: scale };
    } else if (e.touches.length === 1 && scale > 1) {
      dragRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, baseX: offset.x, baseY: offset.y };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const d = distance(e.touches);
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinchRef.current.baseScale * (d / pinchRef.current.baseDist)));
      setScale(nextScale);
    } else if (e.touches.length === 1 && dragRef.current) {
      const dx = e.touches[0].clientX - dragRef.current.startX;
      const dy = e.touches[0].clientY - dragRef.current.startY;
      setOffset(clampOffset(scale, { x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy }));
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) dragRef.current = null;
  };

  /**
   * Capture the currently visible portion of the image (at its native
   * resolution) and hand it back as a File via `onCrop(file)`. This gives
   * OCR / vision LLMs a tight, high-detail crop of just the plate/tag the
   * user zoomed into.
   */
  const captureVisibleCrop = async () => {
    if (!onCrop || !imgWrapRef.current) return;
    setCropping(true);
    try {
      const wrap = imgWrapRef.current;
      const rect = wrap.getBoundingClientRect();
      const cW = rect.width, cH = rect.height;

      // Load the source image at natural resolution.
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = src;
      });
      const iW = img.naturalWidth, iH = img.naturalHeight;

      // object-contain fit: uniform scale so both dims fit inside the container.
      const k = Math.min(cW / iW, cH / iH);   // display px per natural px at scale=1
      const dW = iW * k, dH = iH * k;          // displayed image size at scale=1
      // Image is centered + offset; its displayed size at current zoom is dW*scale × dH*scale.
      const orgX = cW / 2 - (dW * scale) / 2 + offset.x;
      const orgY = cH / 2 - (dH * scale) / 2 + offset.y;

      // Visible portion of the image in display coords (relative to image top-left).
      const visX1 = Math.max(0, -orgX);
      const visY1 = Math.max(0, -orgY);
      const visX2 = Math.min(dW * scale, cW - orgX);
      const visY2 = Math.min(dH * scale, cH - orgY);
      if (visX2 <= visX1 || visY2 <= visY1) {
        setCropping(false);
        return;
      }

      // Convert display coords → natural source coords.
      const natScale = k * scale;              // natural → display multiplier
      const sx = visX1 / natScale;
      const sy = visY1 / natScale;
      const sw = (visX2 - visX1) / natScale;
      const sh = (visY2 - visY1) / natScale;

      // Draw the source region onto a canvas at natural resolution.
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!blob) { setCropping(false); return; }
      const file = new File([blob], `crop-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCrop(file);
      setOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("crop failed", err);
    } finally {
      setCropping(false);
    }
  };

  if (!src) return null;

  return (
    <>
      <div className="relative group inline-block max-w-full">
        <img
          src={src}
          alt={alt}
          className={`cursor-zoom-in object-contain mx-auto block ${className}`}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
          data-testid={testid}
        />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="absolute bottom-1.5 right-1.5 bg-shop/85 border border-shop-strong text-cream p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`${testid}-open-btn`}
          title="Open zoom viewer"
        >
          <Maximize2 size={14} />
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          data-testid={`${testid}-modal`}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {/* Top toolbar */}
          <div className="flex flex-wrap items-center justify-between px-4 py-3 border-b border-shop-strong bg-shop gap-2">
            <div className="font-mono text-xs uppercase tracking-widest text-amber-brass">
              Zoom {scale.toFixed(1)}× &nbsp;·&nbsp; scroll / pinch to zoom, drag to pan
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid={`${testid}-zoom-out`}
                onClick={() => setScale((s) => Math.max(MIN_SCALE, s - 0.5))}
                className="p-2 border border-shop-strong bg-elevated text-cream hover:text-amber-brass"
                title="Zoom out (-)"
              >
                <ZoomOut size={16} />
              </button>
              <button
                type="button"
                data-testid={`${testid}-zoom-in`}
                onClick={() => setScale((s) => Math.min(MAX_SCALE, s + 0.5))}
                className="p-2 border border-shop-strong bg-elevated text-cream hover:text-amber-brass"
                title="Zoom in (+)"
              >
                <ZoomIn size={16} />
              </button>
              <button
                type="button"
                data-testid={`${testid}-reset`}
                onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }}
                className="p-2 border border-shop-strong bg-elevated text-cream hover:text-amber-brass"
                title="Reset (0)"
              >
                <RotateCcw size={16} />
              </button>
              {onCrop && (
                <button
                  type="button"
                  data-testid={`${testid}-use-crop`}
                  onClick={captureVisibleCrop}
                  disabled={cropping}
                  className="ml-2 px-3 py-2 border border-[var(--accent-amber)] bg-[var(--accent-amber)] text-shop font-display uppercase tracking-widest text-xs hover:brightness-110 disabled:opacity-60 flex items-center gap-1.5"
                  title="Send this visible area to OCR"
                >
                  <Crop size={14} /> {cropping ? "Cropping…" : cropLabel}
                </button>
              )}
              <button
                type="button"
                data-testid={`${testid}-close`}
                onClick={() => setOpen(false)}
                className="ml-2 p-2 border border-shop-strong bg-elevated text-cream hover:text-amber-brass"
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Image viewport */}
          <div
            ref={imgWrapRef}
            className="flex-1 overflow-hidden flex items-center justify-center select-none"
            style={{ cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "default" }}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={src}
              alt={alt}
              draggable={false}
              className="max-h-full max-w-full pointer-events-none transition-transform duration-75"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transformOrigin: "center center",
              }}
              data-testid={`${testid}-zoomed-img`}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ZoomablePreview;
